/** @format */

import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion"; // Added AnimatePresence
import { PanelsTopLeft, SwatchBook, Filter, HelpCircle } from "lucide-react"; // Added Filter, HelpCircle
import {
	customizeProduct,
	filterFabricsByGroup,
	getPriceDifference,
	groupFabric,
} from "../../actions/blindActions";
import { addToSwatchCart } from "../../actions/cartActions";
import Customize_Reworked from "./index"; // Import the layout wrapper
import FabricSeriesHeader from "../../components/FabricSeriesHeader";
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Toast } from "./Toast";
import { Link } from "react-router-dom"; // For help links

// --- Main Fabric Step Component ---
const Customize_Fabric = () => {
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) =>
			state.customize?.customizeProduct || { productInfo: {}, optionInfo: {} } // Provide default empty objects
	);
	const swatchCartItems = useSelector(
		(state) => state.swatchCart?.swatchCartItems || []
	); // Default to empty array

	// --- Component State ---
	const [sampleToast, setSampleToast] = useState(false);
	const [fabricGroupFilter, setFabricGroupFilter] = useState(() => {
		// Initialize filter based on Default_Fabric_Type, ensuring case-insensitivity and handling 'All'
		const defaultType = productInfo?.Default_Fabric_Type?.toLowerCase();
		return defaultType === "all" || !defaultType
			? "All"
			: productInfo.Default_Fabric_Type.charAt(0).toUpperCase() +
					productInfo.Default_Fabric_Type.slice(1);
	});
	const [fabricColourFilter, setFabricColourFilter] = useState("All");
	const [fabricGroups, setFabricGroups] = useState([]); // Available transparency groups
	const [fabricColours, setFabricColours] = useState([]); // Available color families
	const [allFabrics, setAllFabrics] = useState([]); // Raw data from API for filtering
	const [allPrices, setAllPrices] = useState([]); // Price difference data
	const [filteredFabrics, setFilteredFabrics] = useState({}); // Grouped & filtered data for display
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null); // Error state

	// Selected fabric state (initialized from Redux or defaults)
	const [selectedGroup, setSelectedGroup] = useState(
		productInfo?.Fabric_Type || ""
	);
	const [selectedFabricCode, setSelectedFabricCode] = useState(
		productInfo?.Fabric_Code || ""
	);
	const [selectedFabric, setSelectedFabric] = useState(
		productInfo?.Fabric || ""
	); // Series name
	const [selectedFabricColor, setSelectedFabricColor] = useState(
		productInfo?.SG_Color || ""
	); // Color name
	const [priceGroup, setPriceGroup] = useState(productInfo?.Price_Group || 0);
	const [selectedOcsName, setSelectedOcsName] = useState(
		productInfo?.OCS_Name || ""
	); // Series name for display/grouping
	const [selectedOcsColour, setSelectedOcsColour] = useState(
		productInfo?.OCS_Colour || ""
	); // OCS color name
	const [selectedOcsDiscount, setSelectedOcsDiscount] = useState(
		productInfo?.Discount || 0
	);

	// --- Derived Values ---
	const measure = optionInfo?.measure;
	const shadeTitle = useMemo(
		() => (productInfo?.Type ? `${productInfo.Type} Shade` : "").toLowerCase(),
		[productInfo?.Type]
	);

	// Function to safely parse and calculate dimension value
	const getDimensionValue = (whole, fraction) => {
		const wholeNum = parseFloat(whole) || 0;
		let fracValue = 0;
		if (fraction && fraction !== "0") {
			try {
				fracValue = new Function(`return ${fraction}`)() || 0;
			} catch (e) {
				console.error("Error evaluating fraction:", fraction, e);
			}
		}
		return wholeNum + fracValue;
	};

	const width = useMemo(
		() => getDimensionValue(optionInfo?.widthWhole, optionInfo?.widthFraction),
		[optionInfo?.widthWhole, optionInfo?.widthFraction]
	);
	const length = useMemo(
		() =>
			getDimensionValue(optionInfo?.lengthWhole, optionInfo?.lengthFraction),
		[optionInfo?.lengthWhole, optionInfo?.lengthFraction]
	);

	// Adjusted width based on mount type
	const adjustedWidth = useMemo(() => {
		switch (measure) {
			case "Tight":
				return width - 0.125; // Use 1/8 inch directly
			case "Cloth":
				return width + 1.0; // Check if this 1" addition is correct for cloth width calculation
			case "Finish":
			default:
				return width;
		}
	}, [width, measure]);

	// --- Animation Variants ---
	const cardVariants = {
		/* ... variants ... */
	};
	const selectVariants = {
		/* ... variants ... */
	};

	// --- Data Fetching and Processing ---

	// Fetch available fabric transparency groups
	const loadGroups = async () => {
		if (!adjustedWidth || !shadeTitle || adjustedWidth <= 0) return; // Guard clause

		try {
			const groupsResult = await groupFabric(adjustedWidth, shadeTitle);
			const formattedGroups = groupsResult
				.map((group) => group.charAt(0).toUpperCase() + group.slice(1))
				.filter((group) => group !== "Null"); // Assuming 'Null' group should be hidden
			setFabricGroups(formattedGroups);

			// Ensure the current filter is valid, reset to 'All' if not
			if (
				fabricGroupFilter !== "All" &&
				!formattedGroups.includes(fabricGroupFilter)
			) {
				setFabricGroupFilter("All");
			}
			// Ensure productInfo error is reset if successful
			if (productInfo?.error) {
				dispatch(
					customizeProduct({
						productInfo: { ...productInfo, error: false },
						optionInfo,
					})
				);
			}
		} catch (error) {
			console.error("Error loading fabric groups:", error);
			setError(
				"Could not load fabric groups. Please check dimensions or try again."
			);
			// Dispatch error state to prevent proceeding?
			dispatch(
				customizeProduct({
					productInfo: { ...productInfo, error: true },
					optionInfo,
				})
			);
			// Redirect might be too abrupt, consider showing error message
			// window.location.href = "/customize/details";
		}
	};

	// Fetch all fabrics, colors, and price differences
	const loadColoursAndPrices = async () => {
		if (!adjustedWidth || !shadeTitle || adjustedWidth <= 0 || length <= 0)
			return; // Guard clause
		setLoading(true);
		setError(null);

		try {
			// Determine the initial group to load (handle 'All' vs specific default)
			const initialGroupToLoad =
				fabricGroupFilter === "All" ? "All" : productInfo?.Fabric_Type || "All";

			// Fetch fabrics based on initial group and 'All' colors
			const fabricsResult = await filterFabricsByGroup(
				adjustedWidth,
				shadeTitle,
				initialGroupToLoad, // Load based on filter or default type
				"All" // Always load all colors initially
			);
			// Fetch price differences
			const pricesResult = await getPriceDifference(
				shadeTitle,
				length,
				adjustedWidth
			);

			// --- Process Fabric Data ---
			let processedFabrics = fabricsResult.seriesColourImage.map((item) => ({
				...item,
				// Normalize image paths
				image: item.image.map((img, j) => {
					let processedImg = img.replace("image/", "/images/");
					if (
						!processedImg.toLowerCase().endsWith(".jpg") &&
						!processedImg.toLowerCase().endsWith(".png") &&
						!processedImg.toLowerCase().endsWith(".jpeg")
					) {
						processedImg += item.colour[j] + ".jpg"; // Append color and default extension if missing
					}
					return processedImg;
				}),
				// Fix potential spelling issues
				colour: item.colour.map((c) => (c === "Berkeley" ? "Berkley" : c)),
				// Ensure all array properties exist, default to empty array if missing
				colourFamily: item.colourFamily || [],
				series: item.series || [],
				code: item.code || [],
				group: item.group || [],
				penetration: item.penetration || [],
				composition: item.composition || [],
				fire_rating: item.fire_rating || [],
				green_rating: item.green_rating || [],
				antimicrobial_rating: item.antimicrobial_rating || [],
				ocs_name: item.ocs_name || [],
				ocs_colour_name: item.ocs_colour_name || [],
				ocs_discount: item.ocs_discount || [],
			}));

			// Extract unique color families for the filter dropdown
			const uniqueColors = [
				...new Set(processedFabrics.flatMap((item) => item.colourFamily)),
			].sort();

			// Update state
			setFabricColours(uniqueColors);
			setAllFabrics(processedFabrics); // Store the raw processed data
			setAllPrices(pricesResult);
			setLoading(false);

			// Apply initial filters immediately after loading
			filterAndGroupFabrics(
				processedFabrics,
				fabricGroupFilter,
				fabricColourFilter
			);

			// Set default selection if none exists in productInfo
			if (productInfo?.Fabric_Code === "" || !productInfo?.Fabric_Code) {
				// Find the first available fabric after initial load and filtering
				const firstAvailableFabric = findFirstFabric(
					processedFabrics,
					fabricGroupFilter,
					fabricColourFilter
				);
				if (firstAvailableFabric) {
					fabricHandler(
						firstAvailableFabric.series[0],
						firstAvailableFabric.colour[0],
						firstAvailableFabric.code[0],
						firstAvailableFabric.group[0],
						firstAvailableFabric.priceGroup,
						firstAvailableFabric.ocs_name[0],
						firstAvailableFabric.ocs_colour_name[0],
						firstAvailableFabric.ocs_discount?.[0] || 0 // Ensure discount defaults to 0
					);
				}
			}
		} catch (err) {
			console.error("Error loading fabric details:", err);
			setError(
				"Could not load fabric details. Please check dimensions or try again."
			);
			setLoading(false);
			// Dispatch error state
			dispatch(
				customizeProduct({
					productInfo: { ...productInfo, error: true },
					optionInfo,
				})
			);
			// Redirect might be too abrupt
			// window.location.href = "/customize/details";
		}
	};

	// Helper to find the first valid fabric after loading/filtering
	const findFirstFabric = (fabrics, groupFilter, colorFilter) => {
		const filtered = filterRawFabrics(fabrics, groupFilter, colorFilter);
		if (filtered.length > 0) {
			// Find the first item within the first series that has valid data
			const firstSeriesKey = filtered[0]?.ocs_name?.[0];
			if (firstSeriesKey) {
				const grouped = groupAndSortFabrics(filtered);
				if (grouped[firstSeriesKey] && grouped[firstSeriesKey].length > 0) {
					return grouped[firstSeriesKey][0];
				}
			}
			// Fallback if grouping fails or first item is invalid
			return filtered[0];
		}
		return null;
	};

	// --- Filtering and Grouping Logic ---

	// Filters the raw `allFabrics` array based on group and color filters
	const filterRawFabrics = (fabrics, groupFilter, colorFilter) => {
		let filtered = fabrics;

		const filterByProperty = (item, propertyName, filterValue) => {
			const matchingIndices = (item[propertyName] || []).reduce(
				(indices, value, index) => {
					if (value?.toLowerCase() === filterValue.toLowerCase()) {
						indices.push(index);
					}
					return indices;
				},
				[]
			);

			if (matchingIndices.length === 0) return null;

			const arrayProperties = [
				"colourFamily",
				"series",
				"colour",
				"image",
				"code",
				"group",
				"penetration",
				"composition",
				"fire_rating",
				"green_rating",
				"antimicrobial_rating",
				"ocs_name",
				"ocs_colour_name",
				"ocs_discount",
			];

			const filteredItem = arrayProperties.reduce((obj, prop) => {
				obj[prop] = matchingIndices
					.map((index) => item[prop]?.[index])
					.filter((v) => v !== undefined && v !== null);
				return obj;
			}, {});

			filteredItem.priceGroup = item.priceGroup; // Keep non-array property

			// Ensure essential data exists after filtering indices
			const requiredProps = ["code", "colour", "image", "series", "ocs_name"];
			const hasRequired = requiredProps.every(
				(prop) => filteredItem[prop]?.length > 0
			);

			return hasRequired ? filteredItem : null;
		};

		if (groupFilter !== "All") {
			filtered = filtered
				.map((item) => filterByProperty(item, "group", groupFilter))
				.filter(Boolean);
		}
		if (colorFilter !== "All") {
			filtered = filtered
				.map((item) => filterByProperty(item, "colourFamily", colorFilter))
				.filter(Boolean);
		}
		return filtered;
	};

	// Groups an array of filtered fabrics by ocs_name and sorts each group by priceGroup
	const groupAndSortFabrics = (filteredFabricsArray) => {
		const grouped = filteredFabricsArray.reduce((acc, item) => {
			(item.ocs_name || []).forEach((seriesName, index) => {
				if (!seriesName) return; // Skip if ocs_name is missing
				if (!acc[seriesName]) acc[seriesName] = [];

				// Create a single-entry item for *this specific ocs_name instance*
				const singleSeriesItem = {
					colourFamily: [item.colourFamily?.[index]],
					series: [item.series?.[index]],
					colour: [item.colour?.[index]],
					image: [item.image?.[index]],
					code: [item.code?.[index]],
					group: [item.group?.[index]],
					penetration: item.penetration?.[index]
						? [item.penetration[index]]
						: [],
					composition: item.composition?.[index]
						? [item.composition[index]]
						: [],
					fire_rating: item.fire_rating?.[index]
						? [item.fire_rating[index]]
						: [],
					green_rating: item.green_rating?.[index]
						? [item.green_rating[index]]
						: [],
					antimicrobial_rating: item.antimicrobial_rating?.[index]
						? [item.antimicrobial_rating[index]]
						: [],
					ocs_name: [seriesName], // Use the current seriesName
					ocs_colour_name: [item.ocs_colour_name?.[index]],
					ocs_discount: item.ocs_discount?.[index]
						? [item.ocs_discount[index]]
						: [],
					priceGroup: item.priceGroup,
				};
				acc[seriesName].push(singleSeriesItem);
			});
			return acc;
		}, {});

		// Sort each group by priceGroup
		Object.keys(grouped).forEach((series) => {
			grouped[series].sort((a, b) => a.priceGroup - b.priceGroup);
		});

		return grouped;
	};

	// Applies filters and updates the `filteredFabrics` state
	const filterAndGroupFabrics = (sourceFabrics, groupFilter, colorFilter) => {
		const rawFiltered = filterRawFabrics(
			sourceFabrics,
			groupFilter,
			colorFilter
		);
		const groupedAndSorted = groupAndSortFabrics(rawFiltered);
		setFilteredFabrics(groupedAndSorted);
	};

	// --- useEffect Hooks ---

	// Load initial groups and colors/prices
	useEffect(() => {
		loadGroups();
		loadColoursAndPrices();
	}, [adjustedWidth, length, shadeTitle]); // Reload if dimensions change

	// Re-filter when filters change
	useEffect(() => {
		// Avoid filtering before data is loaded
		if (!loading && allFabrics.length > 0) {
			filterAndGroupFabrics(allFabrics, fabricGroupFilter, fabricColourFilter);
		}
	}, [fabricGroupFilter, fabricColourFilter, allFabrics, loading]);

	// Dispatch updates to Redux when selection changes
	useEffect(() => {
		// Only dispatch if a valid fabric code is selected
		if (selectedFabricCode) {
			dispatchDetails();
		}
	}, [
		selectedFabricCode,
		selectedFabric,
		selectedFabricColor,
		priceGroup,
		selectedGroup,
		selectedOcsName,
		selectedOcsColour,
		selectedOcsDiscount,
	]); // Include all selected state vars

	// --- Event Handlers ---

	// Update state and dispatch when a fabric swatch is clicked
	const fabricHandler = (
		series,
		colour,
		code,
		group,
		pg,
		ocsName,
		ocsColourName,
		ocsDiscount
	) => {
		setSelectedFabric(series);
		setSelectedFabricColor(colour);
		setSelectedFabricCode(code);
		setSelectedGroup(group);
		setPriceGroup(pg);
		setSelectedOcsName(ocsName);
		setSelectedOcsColour(ocsColourName);
		setSelectedOcsDiscount(ocsDiscount || 0); // Ensure discount is a number
	};

	// Check if a specific swatch is already in the swatch cart
	const existsInCart = (series, img, color, code) => {
		return swatchCartItems.some(
			(item) =>
				item.item.code === code &&
				item.item.series === series && // Use series for comparison if appropriate
				item.item.name === `${series} ${color}` && // Ensure name format matches
				item.item.shade === shadeTitle
		);
	};

	// Add swatch to the swatch cart
	const handleSample = (
		series,
		img,
		color,
		code,
		ocs_name,
		ocs_colour_name
	) => {
		if (swatchCartItems.length >= 10) {
			alert("You can add a maximum of 10 free samples to your cart.");
			return;
		}
		// Use more robust check based on unique properties like code and series
		if (existsInCart(series, img, color, code)) {
			// Maybe show a message saying "Already in cart" instead of silently returning
			console.log("Sample already in cart:", code);
			return;
		}

		const itemPayload = {
			shade: shadeTitle,
			name: `${series} ${color}`, // Consistent naming
			image: img,
			code: code,
			type: series, // Or use 'group' if more appropriate? Check swatch cart usage
			series: series, // Store series name
			ocs_name: ocs_name,
			ocs_colour_name: ocs_colour_name,
		};

		dispatch(addToSwatchCart({ item: itemPayload }));
		setSampleToast(true); // Show confirmation toast
	};

	// --- Dispatch Function ---
	const dispatchDetails = () => {
		// Ensure productInfo exists
		if (!productInfo) return;
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					Fabric_Code: selectedFabricCode,
					Fabric: selectedFabric, // Series name
					SG_Color: selectedFabricColor, // Color name
					Price_Group: priceGroup,
					Fabric_Type: selectedGroup, // Transparency group
					OCS_Name: selectedOcsName, // Display Series Name
					OCS_Colour: selectedOcsColour, // Display Color Name
					Discount: selectedOcsDiscount,
					error: false, // Reset error on successful selection
				},
				optionInfo: {
					...optionInfo,
					selectedColor: selectedFabricColor, // Keep this if used elsewhere
				},
			})
		);
	};

	// --- Component Render ---
	return (
		// Wrap step content in the layout component
		<Customize_Reworked>
			<div className="space-y-8">
				{/* Toast Notification for Added Sample */}
				<Toast
					show={sampleToast}
					type="success"
					message="Sample swatch added to cart!"
					onClose={() => setSampleToast(false)} // Allow closing
					duration={3000} // Auto-close after 3 seconds
				/>

				{/* --- Filters Section --- */}
				<fieldset className="space-y-4">
					<legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<Filter className="w-5 h-5 text-primary" />
						</div>
						Filter Fabrics
						<Link
							to="/guides/productselection"
							className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1"
							target="_blank"
							rel="noopener noreferrer"
						>
							<HelpCircle size={14} /> Fabric Help
						</Link>
					</legend>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{/* Transparency Filter */}
						<motion.div
							variants={selectVariants}
							whileFocus="focus"
							animate="blur"
							transition={{ duration: 0.15 }}
						>
							<label
								htmlFor="filter-group"
								className="block text-sm font-medium mb-1.5 text-gray-700"
							>
								By Transparency
							</label>
							<select
								id="filter-group"
								value={fabricGroupFilter}
								onChange={(e) => setFabricGroupFilter(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none appearance-none transition-all duration-150"
								disabled={loading} // Disable while loading
							>
								{/* Option to show 'All' only if default wasn't specific */}
								{(productInfo?.Default_Fabric_Type?.toLowerCase() === "all" ||
									!productInfo?.Default_Fabric_Type) && (
									<option value="All">All Transparencies</option>
								)}
								{/* Map available groups */}
								{fabricGroups.map((group, i) => (
									<option key={`${group}-${i}`} value={group}>
										{group}
									</option>
								))}
							</select>
						</motion.div>

						{/* Color Filter */}
						<motion.div
							variants={selectVariants}
							whileFocus="focus"
							animate="blur"
							transition={{ duration: 0.15 }}
						>
							<label
								htmlFor="filter-colour"
								className="block text-sm font-medium mb-1.5 text-gray-700"
							>
								By Colour Family
							</label>
							<select
								id="filter-colour"
								aria-label="Fabric Colour Filter"
								value={fabricColourFilter}
								onChange={(e) => setFabricColourFilter(e.target.value)}
								className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none appearance-none transition-all duration-150"
								disabled={loading || fabricColours.length === 0} // Disable if loading or no colors available
							>
								<option value="All">All Colours</option>
								{fabricColours.map((color) => (
									<option key={color} value={color}>
										{color}
									</option>
								))}
							</select>
						</motion.div>
					</div>
				</fieldset>

				{/* --- Fabrics Grid Section --- */}
				<div className="space-y-4 pt-4">
					<h3 className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<PanelsTopLeft className="w-5 h-5 text-primary" />
						</div>
						Available Fabrics
					</h3>

					{/* Conditional Rendering: Loading, Error, No Results, Grid */}
					<AnimatePresence mode="wait">
						{loading ? (
							<motion.div
								key="loading"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="flex flex-col items-center justify-center h-40 text-gray-500"
							>
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
								<p>Loading fabrics...</p>
							</motion.div>
						) : error ? (
							<motion.div
								key="error"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="flex flex-col items-center justify-center h-40 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
							>
								<p className="font-medium text-center">{error}</p>
								<Link
									to="/customize/details"
									className="mt-4 text-sm text-primary hover:underline"
								>
									Go back to Details
								</Link>
							</motion.div>
						) : Object.keys(filteredFabrics).length === 0 ? (
							<motion.div
								key="no-results"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								className="text-center py-12 text-gray-500 bg-gray-100 rounded-lg"
							>
								<p className="font-medium">
									No fabrics found matching your filters.
								</p>
								<p className="text-sm mt-1">
									Try adjusting the Transparency or Colour filters above.
								</p>
							</motion.div>
						) : (
							// --- Fabric Series Grid ---
							<motion.div
								key="fabric-grid"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								// Removed exit animation for smoother filter changes
								// exit={{ opacity: 0 }}
								className="space-y-8"
							>
								{Object.entries(filteredFabrics).map(
									([seriesName, fabricsInSeries]) => (
										<div key={seriesName} className="space-y-4">
											{/* Fabric Series Header */}
											<FabricSeriesHeader
												// Pass necessary info, ensure allPrices is available
												title={fabricsInSeries[0]?.series?.[0] || seriesName} // Fallback title
												headerInfo={{
													ocs_name: seriesName,
													// Safely get the price group of the first fabric in the *overall* list for comparison
													firstOverallPG:
														allFabrics.length > 0
															? allFabrics[0].priceGroup
															: 0,
													// Use the price group of the *current* series being rendered
													currentPG: fabricsInSeries[0]?.priceGroup || 0,
													allPrices: allPrices, // Price difference data
													group: fabricsInSeries[0]?.group?.[0]
														? fabricsInSeries[0].group[0]
																.charAt(0)
																.toUpperCase() +
														  fabricsInSeries[0].group[0].slice(1)
														: "",
													composition:
														fabricsInSeries[0]?.composition?.[0] || "-",
													green_rating:
														fabricsInSeries[0]?.green_rating?.[0] || "-",
													fire_rating:
														fabricsInSeries[0]?.fire_rating?.[0] || "-",
													antimicrobial_rating:
														fabricsInSeries[0]?.antimicrobial_rating?.[0] ||
														"-",
													disc: fabricsInSeries[0]?.ocs_discount?.[0] || 0, // Use fabric specific discount if available
													globalDisc: productInfo?.Global_Discount || 0, // Use global discount from productInfo
												}}
												className="bg-white shadow-sm rounded-xl p-4 border border-gray-200 sticky top-[calc(theme(spacing.16)+1px)] z-10" // Make header sticky below main stepper
											/>

											{/* Fabric Swatches Grid for the current series */}
											<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
												{fabricsInSeries.map((fabric, fabIndex) => {
													const isSelected =
														selectedFabricCode === fabric.code?.[0];
													const isInSwatchCart = existsInCart(
														fabric.series?.[0],
														fabric.image?.[0],
														fabric.colour?.[0],
														fabric.code?.[0]
													);

													return (
														<motion.div
															key={
																fabric.code?.[0] || `${seriesName}-${fabIndex}`
															} // Fallback key
															variants={cardVariants} // Use defined variants
															initial="initial"
															animate="animate"
															// Removed exit variant for smoother filtering
															whileHover={{ y: -3 }} // Subtle lift
															className={`relative rounded-xl border-2 transition-all duration-150 flex flex-col overflow-hidden bg-white ${
																isSelected
																	? "border-primary shadow-lg"
																	: "border-transparent hover:border-gray-300 shadow-sm"
															}`}
														>
															{/* Clickable area for selection */}
															<div
																className="cursor-pointer flex-grow"
																aria-label={`Select ${fabric.series?.[0]} ${fabric.colour?.[0]}`}
																onClick={() =>
																	fabricHandler(
																		fabric.series?.[0],
																		fabric.colour?.[0],
																		fabric.code?.[0],
																		fabric.group?.[0],
																		fabric.priceGroup,
																		fabric.ocs_name?.[0],
																		fabric.ocs_colour_name?.[0],
																		fabric.ocs_discount?.[0] || 0
																	)
																}
															>
																{/* Image with Zoom */}
																<ImageZoomWithButton
																	item={{
																		image: fabric.image?.[0],
																		code: fabric.code?.[0],
																		colour: fabric.colour?.[0],
																	}}
																	isFabricSelected={isSelected}
																	customize={true} // Assuming this prop is needed
																	buttonAction={() => {}} // Provide dummy action or remove button if not needed
																	hideButton={true} // Hide the default zoom button if using swatch button
																/>
																{/* Fabric Name */}
																<div className="p-3 text-center">
																	<p
																		className="font-medium text-sm truncate"
																		title={
																			fabric.ocs_colour_name?.[0] ||
																			fabric.colour?.[0]
																		}
																	>
																		{fabric.ocs_colour_name?.[0] ||
																			fabric.colour?.[0]}
																	</p>
																</div>
															</div>

															{/* Free Sample Button */}
															<button
																onClick={() =>
																	handleSample(
																		fabric.series?.[0],
																		fabric.image?.[0],
																		fabric.colour?.[0],
																		fabric.code?.[0],
																		fabric.ocs_name?.[0],
																		fabric.ocs_colour_name?.[0]
																	)
																}
																disabled={isInSwatchCart} // Disable if already added
																className={`w-full py-2 text-xs font-semibold transition-colors duration-150 ${
																	isInSwatchCart
																		? "bg-gray-200 text-gray-500 cursor-not-allowed"
																		: "bg-primary/10 text-primary hover:bg-primary/20"
																}`}
																aria-label={
																	isInSwatchCart
																		? `Sample ${fabric.colour?.[0]} added`
																		: `Add free sample ${fabric.colour?.[0]}`
																}
															>
																{isInSwatchCart
																	? "Sample Added"
																	: "Get Free Sample"}
															</button>
														</motion.div>
													);
												})}
											</div>
										</div>
									)
								)}
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Fabric;
